import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Contrato } from '../types';
import contractsService from '../services/contracts.service';
import { 
  PencilIcon, 
  TrashIcon, 
  DocumentTextIcon, 
  PlusIcon,
  ArrowPathIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/format';
import Modal from '../components/UI/Modal';
import { useToast } from '../context/ToastContext';

function ContratoDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [contrato, setContrato] = useState<Contrato | null>(null);
  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);

  const loadContrato = React.useCallback(async () => {
    if (!id) return;
    try {
      const data = await contractsService.getContratoById(Number(id));
      setContrato(data);
    } catch (error) {
      console.error('Error loading contrato:', error);
      showToast('Error al cargar el contrato', 'error');
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  useEffect(() => {
    loadContrato();
  }, [loadContrato]);

  const handleDelete = async () => {
    if (!contrato) return;
    if (!window.confirm('¿Está seguro de eliminar este contrato?')) return;

    try {
      await contractsService.deleteContrato(contrato.id);
      showToast('Contrato eliminado exitosamente', 'success');
      navigate('/contratos');
    } catch (error) {
      console.error('Error deleting contrato:', error);
      showToast('Error al eliminar el contrato', 'error');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !contrato) return;
    
    setIsUploading(true);
    const files = Array.from(e.target.files);
    let successCount = 0;
    let errorCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('archivo', file);
      
      try {
        await contractsService.uploadArchivo(contrato.id, formData);
        successCount++;
      } catch (error) {
        console.error('Error uploading file:', error);
        errorCount++;
      }
    }
    
    setIsUploading(false);
    
    if (successCount > 0) {
      showToast(`${successCount} archivo(s) subido(s) exitosamente`, 'success');
      loadContrato(); // Refresh list
    }
    
    if (errorCount > 0) {
      showToast(`Error al subir ${errorCount} archivo(s)`, 'error');
    }
    
    // Reset input
    e.target.value = '';
  };

  const confirmDeleteFile = (archivoId: number) => {
    setFileToDelete(archivoId);
    setDeleteModalOpen(true);
  };

  const handleDeleteFile = async () => {
    if (!contrato || !fileToDelete) return;

    try {
      await contractsService.deleteArchivo(contrato.id, fileToDelete);
      showToast('Archivo eliminado exitosamente', 'success');
      loadContrato();
    } catch (error) {
      console.error('Error deleting file:', error);
      showToast('Error al eliminar el archivo', 'error');
    } finally {
      setFileToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!contrato) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">Contrato no encontrado</h3>
      </div>
    );
  }

  const pagosProgramados = contrato.pagosProgramados || [];
  const pagosReales = contrato.pagos || [];
  const totalPagado = pagosReales.reduce((sum, pago) => sum + pago.monto, 0);
  const porcentajePagado = contrato.valorTotal > 0 ? (totalPagado / contrato.valorTotal) * 100 : 0;

  return (
    <div className="space-y-6">
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Archivo"
        message="¿Está seguro de que desea eliminar este archivo? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        type="danger"
        onConfirm={handleDeleteFile}
      />

      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/contratos')}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Volver a contratos
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Contrato #{contrato.numeroContrato}</h1>
            <p className="mt-2 text-sm text-gray-700">{contrato.cliente?.nombre}</p>
          </div>
          <div className="flex space-x-3">
            <Link
              to={`/contratos/${contrato.id}/editar`}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Editar
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              Eliminar
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Información General</h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Cliente</dt>
                <dd className="mt-1 text-sm text-gray-900">{contrato.cliente?.nombre}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Objeto</dt>
                <dd className="mt-1 text-sm text-gray-900">{contrato.objeto}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Fecha Inicio</dt>
                <dd className="mt-1 text-sm text-gray-900">{new Date(contrato.fechaInicio).toLocaleDateString()}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Fecha Fin</dt>
                <dd className="mt-1 text-sm text-gray-900">{new Date(contrato.fechaFin).toLocaleDateString()}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Valor Total</dt>
                <dd className="mt-1 text-sm text-gray-900">{formatCurrency(Number(contrato.valorTotal))}</dd>
              </div>
              <div className="sm:col-span-1">
                <dt className="text-sm font-medium text-gray-500">Estado</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                    contrato.estado === 'vigente' 
                      ? 'bg-green-50 text-green-700 ring-green-600/20' 
                      : 'bg-red-50 text-red-700 ring-red-600/20'
                  }`}>
                    {contrato.estado === 'vigente' ? 'Vigente' : 'No Vigente'}
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Items del Contrato */}
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 mb-4">Items del Contrato</h3>
            {contrato.detalles && contrato.detalles.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr>
                      <th scope="col" className="py-3 pl-4 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Descripción</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Cant.</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Valor Unit.</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Desc.</th>
                      <th scope="col" className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-500">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {contrato.detalles.map((detalle) => (
                      <tr key={detalle.id}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">{detalle.descripcion}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">{detalle.cantidad}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">{formatCurrency(Number(detalle.precioUnitario))}</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">{detalle.descuentoPct}%</td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-right text-gray-500">{formatCurrency(Number(detalle.subtotalNeto))}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td colSpan={4} className="px-4 py-3 text-sm text-right text-gray-900">Total:</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{formatCurrency(Number(contrato.totalNeto || 0))}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No hay items en este contrato. Edita el contrato para agregar items.
              </div>
            )}
          </div>

          {/* Documentos */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">Documentos</h2>
              <label className={`cursor-pointer inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white ${isUploading ? 'bg-primary-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}>
                {isUploading ? (
                  <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PlusIcon className="h-4 w-4 mr-2" />
                )}
                {isUploading ? 'Subiendo...' : 'Subir Archivo'}
                <input
                  type="file"
                  className="hidden"
                  multiple
                  disabled={isUploading}
                  onChange={handleFileUpload}
                />
              </label>
            </div>

            {contrato.archivos && contrato.archivos.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {contrato.archivos.map((archivo: any) => (
                  <li key={archivo.id} className="py-3 flex justify-between items-center">
                    <div className="flex items-center">
                      <DocumentTextIcon className="h-8 w-8 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{archivo.nombreOriginal}</p>
                        <p className="text-xs text-gray-500">
                          {(archivo.tamaño / 1024).toFixed(1)} KB • {new Date(archivo.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <a
                        href={archivo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-900 text-sm font-medium"
                      >
                        Ver
                      </a>
                      <button
                        onClick={() => confirmDeleteFile(archivo.id)}
                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                      >
                        Eliminar
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-8 text-gray-500 text-sm">
                No hay documentos adjuntos.
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Resumen de Pagos</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Total Pagado</dt>
                <dd className="mt-1 text-2xl font-semibold text-green-600">
                  ${totalPagado.toLocaleString('es-CO')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Pendiente por Pagar</dt>
                <dd className="mt-1 text-2xl font-semibold text-orange-600">
                  ${(contrato.valorTotal - totalPagado).toLocaleString('es-CO')}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Progreso</dt>
                <dd className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${Math.min(porcentajePagado, 100)}%` }}
                    ></div>
                  </div>
                  <span className="text-sm text-gray-600 mt-1 block">
                    {porcentajePagado.toFixed(1)}%
                  </span>
                </dd>
              </div>
            </dl>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Acciones</h3>
            <div className="space-y-3">
              <Link
                to={`/pagos/nuevo?contratoId=${contrato.id}`}
                className="block w-full text-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
              >
                <PlusIcon className="h-4 w-4 inline mr-2" />
                Registrar Pago
              </Link>
              <Link
                to={`/propuestas/nuevo?contratoId=${contrato.id}`}
                className="block w-full text-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Crear Propuesta
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Pagos Programados */}
      {pagosProgramados.length > 0 && (
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Pagos Programados</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cuota
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha Programada
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagosProgramados.map((pago) => (
                    <tr key={pago.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Pago #{pago.id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(pago.fecha).toLocaleDateString('es-CO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        ${pago.monto.toLocaleString('es-CO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            pago.estado === 'pagado'
                              ? 'bg-green-100 text-green-800'
                              : pago.estado === 'cancelado'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {pago.estado}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Pagos Realizados */}
      {pagosReales.length > 0 && (
        <div className="mt-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Pagos Realizados</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha de Pago
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monto
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Método de Pago
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referencia
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pagosReales.map((pago) => (
                    <tr key={pago.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(pago.fechaPago).toLocaleDateString('es-CO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        ${pago.monto.toLocaleString('es-CO')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pago.metodoPago}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {pago.referencia || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/pagos/${pago.id}`}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContratoDetail;